import { useState } from "react";
import {
  useGetStrategyLibraryQuery,
  useGetStrategyDetailQuery,
  useConsultStrategyMutation,
  useGetStrategyConsultsQuery,
  useRunLibraryEngineNowMutation,
  useGetLibraryEngineRunsQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { EngineStateBanner } from "@/components/domain/EngineStateBanner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";

/**
 * Strategy page — admin only.
 *
 * Three sections:
 *  1) Library summary + 12 strategy cards (click to expand for full
 *     entry/exit/sizing rules + source).
 *  2) Manual consult form — type a symbol, ask Gemini "what does the
 *     library say about this right now?" Returns ranked candidates +
 *     verdict + entry params.
 *  3) Recent consults log — every consult ever made, with the
 *     decision (use_strategy | skip), strategy_id chosen, and
 *     Gemini's rationale.
 *
 * The veto layer (enabled per-engine via `strategy_advisor_enabled_engines`
 * setting) does NOT live here — it's a server setting. This page is
 * for browsing the library and running ad-hoc consults.
 */
export default function Strategy() {
  const lib = useGetStrategyLibraryQuery();
  const consults = useGetStrategyConsultsQuery(30, { pollingInterval: 60000 });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [consultSym, setConsultSym] = useState("BTC/USDT");
  const [consult, { isLoading: consulting, data: consultResult, reset: resetConsult }] =
    useConsultStrategyMutation();

  async function runConsult() {
    const sym = consultSym.trim().toUpperCase();
    if (!sym.includes("/")) {
      toast.error("Symbol must be like BTC/USDT");
      return;
    }
    try {
      await consult({ symbol: sym, n_candidates: 3 }).unwrap();
    } catch (e: any) {
      toast.error(`Consult failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Strategy library</h1>
        <p className="text-sm text-muted">
          Curated playbook of vetted trading strategies. The advisor uses RAG to
          retrieve the best-matching ones for current market conditions, then
          asks Gemini to pick + parameterize. Engines opt in to the veto layer
          via{" "}
          <code className="text-xs bg-muted/30 px-1 rounded">
            strategy_advisor_enabled_engines
          </code>{" "}
          setting.
        </p>
      </header>

      <EngineStateBanner
        enabledKey="library_engine_enabled"
        label="Autonomous library engine"
        hostHint="Governor auto-arms this in favorable BTC regime."
      />

      {/* === LIBRARY SUMMARY === */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-baseline gap-2 flex-wrap">
            <span>Library ({lib.data?.total ?? "…"})</span>
            {lib.data && (
              <span className="text-xs text-muted font-normal">
                schema v{lib.data.schema_version} · updated {lib.data.last_updated}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lib.isLoading && <Skeleton className="h-24 w-full" />}
          {lib.data && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="text-muted">by category:</span>
                {Object.entries(lib.data.by_category).map(([k, v]) => (
                  <Badge key={k} tone="info">
                    {k}: {v}
                  </Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="text-muted">fits regime:</span>
                {Object.entries(lib.data.by_regime_fit).map(([k, v]) => (
                  <Badge key={k} tone="good">
                    {k}: {v}
                  </Badge>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
                {lib.data.ids.map((id) => (
                  <StrategyCard
                    key={id}
                    id={id}
                    expanded={expanded === id}
                    onToggle={() => setExpanded(expanded === id ? null : id)}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* === AUTONOMOUS LIBRARY ENGINE === */}
      <LibraryEngineCard />

      {/* === MANUAL CONSULT === */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-baseline gap-2">
            <span>Run consult</span>
            <span className="text-xs text-muted font-normal">
              ad-hoc: ask the library + Gemini what to do on this symbol right
              now
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              className="border rounded px-3 py-1 text-sm font-mono bg-bg text-fg"
              value={consultSym}
              onChange={(e) => setConsultSym(e.target.value)}
              placeholder="BTC/USDT"
            />
            <Button size="sm" onClick={runConsult} disabled={consulting}>
              {consulting ? "Asking Gemini…" : "Consult"}
            </Button>
            {consultResult && (
              <Button size="sm" variant="ghost" onClick={() => resetConsult()}>
                Clear
              </Button>
            )}
          </div>
          {consultResult && <ConsultResultView result={consultResult} />}
        </CardContent>
      </Card>

      {/* === RECENT CONSULTS LOG === */}
      <Card>
        <CardHeader>
          <CardTitle>Recent consults</CardTitle>
        </CardHeader>
        <CardContent>
          {consults.isLoading && <Skeleton className="h-24 w-full" />}
          {!consults.isLoading && (!consults.data || consults.data.length === 0) && (
            <div className="text-sm text-muted">
              No consults yet. Run one above or wait for an engine veto to fire.
            </div>
          )}
          {consults.data && consults.data.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH>Time</TH>
                  <TH>Symbol</TH>
                  <TH>Decision</TH>
                  <TH>Strategy</TH>
                  <TH>Confidence</TH>
                  <TH>Rationale</TH>
                </TR>
              </THead>
              <TBody>
                {consults.data.map((r) => (
                  <TR key={r.id}>
                    <TD className="text-xs whitespace-nowrap">{fmtTime(r.ts)}</TD>
                    <TD className="font-mono">{r.symbol}</TD>
                    <TD>
                      <Badge tone={r.decision === "use_strategy" ? "good" : "muted"}>
                        {r.decision}
                      </Badge>
                    </TD>
                    <TD className="font-mono text-xs">
                      {r.strategy_id ?? <span className="text-muted">—</span>}
                    </TD>
                    <TD className="text-xs">
                      {r.confidence ? (r.confidence * 100).toFixed(0) + "%" : "—"}
                    </TD>
                    <TD className="text-xs max-w-md">{r.rationale}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LibraryEngineCard() {
  const runs = useGetLibraryEngineRunsQuery(20, { pollingInterval: 60000 });
  const [runNow, { isLoading: running }] = useRunLibraryEngineNowMutation();

  async function onRun() {
    try {
      const r: any = await runNow().unwrap();
      const buys = (r?.results || []).reduce(
        (a: number, u: any) => a + (u?.buys ?? 0), 0,
      );
      toast.success(`tick complete — ${buys} buy(s) placed`);
    } catch (e: any) {
      toast.error(`tick failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  const lastRun = runs.data && runs.data[0];
  const totalRecent = (runs.data || []).reduce(
    (a: number, r: any) => a + (r.buys_placed ?? 0), 0,
  );

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-baseline gap-2 flex-wrap">
          <span>Autonomous library engine</span>
          <StalenessBadge ts={lastRun?.ts} warnHours={1} staleHours={4} veryStaleHours={24} label="last tick" />
          <span className="text-xs text-muted font-normal">
            scans whitelist → consults library → executes BUYs
          </span>
        </CardTitle>
        <Button size="sm" onClick={onRun} disabled={running}>
          {running ? "Running tick…" : "Run tick now"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {lastRun && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
            <div>
              <div className="text-muted">last tick</div>
              <div className="font-mono">{fmtTime(lastRun.ts)}</div>
            </div>
            <div>
              <div className="text-muted">scanned</div>
              <div className="font-semibold">{lastRun.symbols_scanned ?? "—"}</div>
            </div>
            <div>
              <div className="text-muted">consults</div>
              <div className="font-semibold">{lastRun.consults_made ?? "—"}</div>
            </div>
            <div>
              <div className="text-muted">buys (last tick)</div>
              <div className={`font-semibold ${lastRun.buys_placed > 0 ? "text-green-500" : ""}`}>
                {lastRun.buys_placed ?? 0}
              </div>
            </div>
            <div>
              <div className="text-muted">buys (last 20 ticks)</div>
              <div className="font-semibold">{totalRecent}</div>
            </div>
          </div>
        )}

        {runs.data && runs.data.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>Time</TH>
                <TH>Scanned</TH>
                <TH>Consults</TH>
                <TH>Buys</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {runs.data.slice(0, 10).map((r: any) => (
                <TR key={r.id}>
                  <TD className="text-xs whitespace-nowrap">{fmtTime(r.ts)}</TD>
                  <TD>{r.symbols_scanned}</TD>
                  <TD>{r.consults_made}</TD>
                  <TD className={r.buys_placed > 0 ? "font-semibold text-green-500" : ""}>
                    {r.buys_placed}
                  </TD>
                  <TD>
                    <Badge tone={r.status === "ok" ? "good" : "muted"}>
                      {r.status}
                    </Badge>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}

        {(!runs.data || runs.data.length === 0) && (
          <Alert tone="info">
            No ticks yet. Click "Run tick now" or wait — the cron fires on its
            configured interval. Enable per-user via{" "}
            <code className="text-xs">user_settings.library_engine_enabled=1</code>.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function StrategyCard({
  id,
  expanded,
  onToggle,
}: {
  id: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const detail = useGetStrategyDetailQuery(id, { skip: !expanded });
  const s = detail.data;

  return (
    <div
      className={`border rounded p-2 transition-colors ${
        expanded ? "border-primary/50 bg-accent/5" : "border-border hover:border-primary/30"
      } cursor-pointer`}
      onClick={onToggle}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-mono text-xs">{id}</div>
        <div className="text-xs text-muted">{expanded ? "−" : "+"}</div>
      </div>
      {expanded && (
        <div className="mt-2 text-xs space-y-2">
          {detail.isLoading && <Skeleton className="h-16 w-full" />}
          {s && (
            <>
              <div>
                <div className="font-semibold text-sm">{s.name}</div>
                <div className="text-muted">
                  {s.category} · timeframe ≥ {s.min_timeframe} · win{" "}
                  {(s.expected_win_rate * 100).toFixed(0)}% · R:R {s.rr_ratio}
                </div>
              </div>
              <div>
                <div className="text-muted mb-0.5">fits:</div>
                <div className="flex flex-wrap gap-1">
                  {s.fits_regime.map((r) => (
                    <Badge key={r} tone="good">
                      {r}
                    </Badge>
                  ))}
                  {s.avoids_regime.length > 0 && (
                    <>
                      <span className="text-muted ml-2">avoids:</span>
                      {s.avoids_regime.map((r) => (
                        <Badge key={r} tone="bad">
                          {r}
                        </Badge>
                      ))}
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="text-muted mb-0.5">entry:</div>
                <ul className="list-disc ml-4 space-y-0.5">
                  {s.entry.conditions.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
                <div className="text-accent mt-1">trigger: {s.entry.trigger}</div>
              </div>
              <div>
                <div className="text-muted mb-0.5">exit:</div>
                <pre className="text-[10px] whitespace-pre-wrap font-mono bg-muted/20 rounded p-1">
                  {JSON.stringify(s.exit, null, 2)}
                </pre>
              </div>
              <div>
                <div className="text-muted mb-0.5">sizing:</div>
                <pre className="text-[10px] whitespace-pre-wrap font-mono bg-muted/20 rounded p-1">
                  {JSON.stringify(s.sizing, null, 2)}
                </pre>
              </div>
              <div className="text-muted/80 italic">{s.notes}</div>
              <div className="text-[10px] text-muted">source: {s.source}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ConsultResultView({ result }: { result: any }) {
  if (result.status !== "ok") {
    return <Alert tone="warn">{result.status}: {result.reason ?? ""}</Alert>;
  }
  const v = result.verdict ?? {};
  const c = result.conditions ?? {};
  const cands = result.candidates_considered ?? [];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {Object.entries({
          price: c.price, regime: c.regime,
          rsi_1h: c.rsi_1h, rsi_daily: c.rsi_daily,
          z_score_1h: c.z_score_1h, bb_squeeze_percentile: c.bb_squeeze_percentile,
          vwap_distance_pct: c.vwap_distance_pct, adx_daily: c.adx_daily,
          donchian_position_20d: c.donchian_position_20d,
          is_nr7_daily: String(c.is_nr7_daily),
          vol_spike_x: c.vol_spike_x, dist_from_24h_high_pct: c.dist_from_24h_high_pct,
        })
          .filter(([, v]) => v !== null && v !== undefined && v !== "undefined")
          .map(([k, val]) => (
            <div key={k}>
              <div className="text-muted">{k}</div>
              <div className="font-mono font-semibold">{String(val)}</div>
            </div>
          ))}
      </div>

      <div>
        <div className="text-xs text-muted mb-1">RAG candidates (top {cands.length}):</div>
        <div className="flex flex-wrap gap-1">
          {cands.map((c: any) => (
            <Badge key={c.id} tone="info" title={c.name}>
              {c.id} ({c.score})
            </Badge>
          ))}
        </div>
      </div>

      <div className="border rounded p-3">
        <div className="flex items-baseline gap-2 flex-wrap">
          <Badge tone={v.decision === "use_strategy" ? "good" : "warn"}>
            {String(v.decision).toUpperCase()}
          </Badge>
          {v.strategy_id && (
            <span className="font-mono text-sm">{v.strategy_id}</span>
          )}
          <span className="text-xs text-muted">
            confidence {((v.confidence ?? 0) * 100).toFixed(0)}% · model {result.model}
          </span>
        </div>
        <div className="text-sm mt-1">{v.rationale}</div>
        {v.parameters?.action === "BUY" && (
          <div className="mt-2 text-xs space-y-0.5">
            <div>
              <span className="text-muted">entry:</span>{" "}
              <span className="font-mono">{v.parameters.entry_price}</span>
              {" → target "}
              <span className="font-mono">{v.parameters.target_price}</span>
              {" / stop "}
              <span className="font-mono">{v.parameters.stop_price}</span>
            </div>
            <div>
              <span className="text-muted">size:</span>{" "}
              {v.parameters.size_pct_equity}% of equity
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
